import { useEffect, useMemo, useState } from 'react';
import { RepeatIcon } from 'lucide-react';
import { useCart, useCartActions } from '../../contexts/StoreContext';
import { useSite } from '../../contexts/SiteContext';
import { policyCopy } from '../../data/policies';
import { euro } from '../../core/format/currency';
import { today } from '../../core/format/dates';
import { boxRange, planBoxes, workingCadences } from '../../core/rules/refillPlan';
import { refillItemsFromProducts, refillRules } from '../../utils/refill';
import { IntervalChoice } from '../shared/IntervalChoice';

/**
 * Wat er bijgevuld wordt, als één regel: "elke X weken een doos met wat op raakt", met de
 * bandbreedte van de dozen. Alleen ritmes waarbij elke doos het minimum haalt (slim bijvullen).
 * Haalt geen enkel ritme het: eerlijk zeggen en iets laten toevoegen. De bestelling stuurt het ritme mee.
 */
export function CartRhythm() {
  const {
    lines,
    hasSubscription,
    subscriptionIntervalWeeks
  } = useCart();
  const {
    setSubscriptionInterval
  } = useCartActions();
  const {
    policy
  } = useSite();
  const [editing, setEditing] = useState(false);
  const rules = refillRules(policy.subscription);
  const start = today();
  const items = useMemo(() => refillItemsFromProducts(lines.filter(l => l.subscribe).map(l => ({
    product: l.product,
    qty: l.qty
  })), start), [lines, start]);
  const working = useMemo(() => workingCadences(items, rules, start), [items, rules.minBoxValue, rules.maxUnitsPerProduct, rules.bufferDays, rules.intervals, start]); // eslint-disable-line react-hooks/exhaustive-deps
  const valid = working.includes(subscriptionIntervalWeeks);

  // Het gekozen ritme werkt niet (meer) met deze producten: neem het voorstel.
  useEffect(() => {
    if (hasSubscription && working.length && !valid) setSubscriptionInterval(working[0]);
  }, [hasSubscription, working, valid, setSubscriptionInterval]);
  if (!hasSubscription) return null;
  if (!working.length) {
    return <div role="status" className="mt-2 bg-sand p-4 text-[13px] leading-relaxed text-ink" data-id="element-6784" data-mp="1jy799mcfbimp">
        <p className="flex items-start gap-2.5" data-id="element-6785" data-mp="1a7767eywa68e">
          <RepeatIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6786" data-mp="1eac577jrnpsa" />
          <span data-id="element-6787" data-mp="10b3zrwx0flfn">
            Bijvullen kan vanaf {euro(rules.minBoxValue)} per doos, en met alleen dit kom je daar niet aan.
            <span className="block text-muted" data-id="element-6788" data-mp="159xfzx3olqji">Zet nog een product op bijvullen (bijvoorbeeld je reiniger), of bestel het nu eenmalig.</span>
          </span>
        </p>
      </div>;
  }
  const range = boxRange(planBoxes(items, {
    intervalWeeks: subscriptionIntervalWeeks,
    rules,
    earliest: start,
    count: 6
  }));
  return <div className="mt-2 bg-blush p-4 text-[13px] leading-relaxed text-ink" data-id="element-6789" data-mp="1j72hdjsga18f">
      <p className="flex items-start gap-2.5" data-id="element-6790" data-mp="1o6ou7s4d9fgp">
        <RepeatIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6791" data-mp="2by42s2mqkt0e" />
        <span className="flex-1" data-id="element-6792" data-mp="2gp2mlc6xo98e">
          <span id="cart-rhythm-title" data-id="element-6793" data-mp="2gpcb0vcby8bz">Elke {subscriptionIntervalWeeks} weken een doos, alleen wat bijna op is</span>
          <span className="block text-muted" data-id="element-6794" data-mp="57r2xlxgr7xp">
            {range && (range.min === range.max ? `${euro(range.min)} per doos. ` : `Meestal ${euro(range.min)} tot ${euro(range.max)} per doos. `)}
            Nooit een overschot; pauzeren of stoppen kan altijd.
          </span>
        </span>
        {working.length > 1 && <button type="button" onClick={() => setEditing(e => !e)} aria-expanded={editing} className="shrink-0 text-ink underline underline-offset-4 hover:text-clay" data-id="element-6795" data-mp="3ig3uor30f2ge">
            {editing ? 'Klaar' : 'Wijzigen'}
          </button>}
      </p>
      {editing && working.length > 1 && <div className="mt-3 pl-[1.625rem]" data-id="element-6796" data-mp="3mhp2hy0osqjq">
          <IntervalChoice compact intervals={working} value={subscriptionIntervalWeeks} onChange={setSubscriptionInterval} labelledBy="cart-rhythm-title" data-id="element-6797" data-mp="3mgnoplsadpyb" />
          <p className="mt-2 text-[12px] text-muted" data-id="element-6798" data-mp="11rhuix8mb6nc">
            Vaker = kleinere dozen, minder vaak = grotere. {policyCopy.subscriptionGift}.
          </p>
        </div>}
    </div>;
}